"use client";

import { useId, useState } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import SectionEyebrow from "@/components/ui/SectionEyebrow";
import Footer from "@/components/layout/Footer";
import FAQAccordion from "@/components/ui/FAQAccordion";
import { CtaLink } from "@/components/CtaLink";
import {
  EVERY_PLAN,
  FeatureItem,
  PLANS,
  PlanCard,
  AudienceTabs,
  PlanOptions,
  planGridClass,
  plansFor,
  type Audience,
  type Billing,
  type Currency,
} from "@/components/sections/PricingPlans";
import { pricingFaqs as faqs } from "@/lib/faqs";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

const SERVICES = [
  {
    eyebrow: "Done for you",
    name: "AI Consulting",
    price: "By enquiry",
    features: [
      "AI strategy for your firm",
      "Custom agent development",
      "Workflow audits & automation design",
      "Integration with your existing tools",
      "Ongoing advisory & support",
    ],
    href: "/consulting",
  },
  {
    eyebrow: "Done with you",
    name: "AI Training",
    price: "By enquiry",
    features: [
      "Hands-on team workshops",
      "Tool mastery: learn to use the best tool and model for the task",
      "Your team ships their first agent in a day",
      "Safe use of AI with client data",
      "Best-practice playbooks",
    ],
    href: "/training",
  },
];

const TEAM_PLAN = PLANS.find((p) => p.id === "team")!;

export default function PricingPage() {
  const [audience, setAudience] = useState<Audience>("solo");
  const [billing, setBilling] = useState<Billing>("monthly");
  const currency: Currency = "USD";
  const reduce = !!useReducedMotion();
  const panelId = useId();
  const plans = plansFor(audience);

  const fadeUp = reduce ? {} : { opacity: 0, y: 18 };

  return (
    <main id="main-content" className="bg-section">
      <div aria-hidden="true" style={{ height: "var(--announcement-h, 0px)" }} />
      <section
        className="relative overflow-hidden pt-32 pb-14 md:pt-40 md:pb-20"
        aria-labelledby="pricing-heading"
      >
        <div className="relative z-10 mx-auto w-full max-w-[1320px] px-6 text-center md:px-12">
          <SectionEyebrow label="Pricing" />
          <motion.h1
            id="pricing-heading"
            className="heading-pure mx-auto font-heading"
            style={{
              fontSize: "clamp(2.6rem, 1.4rem + 4.2vw, 4.75rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.03em",
              fontWeight: 560,
            }}
            initial={fadeUp}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE_OUT }}
          >
            Simple pricing.
            <br />
            Serious leverage.
          </motion.h1>
          <motion.p
            className="mx-auto mt-6 max-w-[38rem] text-on-dark/60 fluid-body-lg"
            initial={fadeUp}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: EASE_OUT }}
          >
            {audience === "solo"
              ? "Start free. Upgrade when you need more credits."
              : `Team plans start at ${TEAM_PLAN.price[currency].monthly} per seat, minimum 5 seats. Enterprise is scoped to your firm.`}
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col items-center justify-center gap-5"
            initial={fadeUp}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: EASE_OUT }}
          >
            <AudienceTabs
              audience={audience}
              onChange={setAudience}
              panelId={panelId}
            />
            <PlanOptions
              audience={audience}
              billing={billing}
              onBillingChange={setBilling}
            />
          </motion.div>
        </div>
      </section>

      <section
        className="pb-[var(--fluid-section-sm)]"
        aria-labelledby="plans-heading"
      >
        <h2 id="plans-heading" className="sr-only">
          Plans: {plans.map((p) => p.name).join(" and ")}
        </h2>
        <div className="mx-auto max-w-[1320px] px-6 md:px-12">
          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`${panelId}-tab-${audience}`}
            className={`mx-auto grid grid-cols-1 items-stretch gap-5 pt-4 ${planGridClass(
              plans.length,
            )}`}
          >
            {plans.map((plan, i) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                billing={billing}
                currency={currency}
                index={i}
                reduce={reduce}
              />
            ))}
          </div>

          <motion.ul
            className="mx-auto mt-12 flex max-w-[1180px] flex-wrap items-center justify-center gap-x-10 gap-y-4 rounded-[22px] border border-hairline px-7 py-6"
            initial={reduce ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: EASE_OUT }}
          >
            {EVERY_PLAN.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-2.5 whitespace-nowrap text-[13px] text-on-dark/70"
              >
                <Icon
                  size={15}
                  strokeWidth={2}
                  className="shrink-0"
                  style={{ color: "hsl(var(--gold-ink))" }}
                  aria-hidden="true"
                />
                {label}
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      <section
        className="pb-[var(--fluid-section)]"
        aria-labelledby="services-heading"
      >
        <div className="mx-auto max-w-[1180px] px-6 md:px-12">
          <div className="mx-auto mb-12 max-w-[720px] text-center">
            <SectionEyebrow label="Services" />
            <motion.h2
              id="services-heading"
              className="heading-pure font-heading"
              style={{
                fontSize: "clamp(1.9rem, 1rem + 3vw, 3.2rem)",
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                fontWeight: 560,
              }}
              initial={fadeUp}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: EASE_OUT }}
            >
              Need more than the platform?
            </motion.h2>
            <motion.p
              className="mx-auto mt-4 max-w-[46ch] text-on-dark/60 fluid-body-lg"
              initial={fadeUp}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1, ease: EASE_OUT }}
            >
              We also build custom agents and train teams. Every engagement is
              scoped to your firm, so tell us what you need and we&apos;ll put
              together a proposal.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {SERVICES.map((service, i) => (
              <motion.div
                key={service.name}
                className="relative flex flex-col overflow-hidden rounded-[32px] border border-hairline bg-panel-solid p-8 md:p-10"
                initial={reduce ? false : { opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, ease: EASE_OUT, delay: i * 0.08 }}
              >
                <div className="relative flex flex-1 flex-col">
                  <p className="font-accent text-[10px] font-medium uppercase tracking-[0.24em] text-eyebrow">
                    {service.eyebrow}
                  </p>
                  <h3
                    className="mt-4 font-heading text-on-dark"
                    style={{
                      fontSize: "clamp(1.5rem, 1.1rem + 1.2vw, 2rem)",
                      lineHeight: 1.1,
                      letterSpacing: "-0.025em",
                      fontWeight: 480,
                    }}
                  >
                    {service.name}
                  </h3>
                  <p
                    className="heading-pure mt-2 font-heading"
                    style={{
                      fontSize: "1.6rem",
                      lineHeight: 1,
                      letterSpacing: "-0.03em",
                      fontWeight: 480,
                    }}
                  >
                    {service.price}
                  </p>

                  <div
                    className="my-6 h-px w-full"
                    style={{ background: "var(--hairline)" }}
                    aria-hidden="true"
                  />

                  <ul className="flex flex-1 flex-col gap-2.5">
                    {service.features.map((feature) => (
                      <FeatureItem key={feature}>{feature}</FeatureItem>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link href={service.href} className="btn-plan flex-1">
                      Learn more
                    </Link>
                    <Link href="/book" className="btn-outline-gold flex-1">
                      Enquire
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="pb-[var(--fluid-section)]"
        aria-labelledby="faq-heading"
      >
        <div className="mx-auto max-w-[860px] px-6 text-center md:px-12">
          <SectionEyebrow label="Questions" />
          <motion.h2
            id="faq-heading"
            className="heading-pure font-heading"
            style={{
              fontSize: "clamp(1.9rem, 1rem + 3vw, 3.2rem)",
              lineHeight: 1.06,
              letterSpacing: "-0.02em",
              fontWeight: 560,
            }}
            initial={fadeUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: EASE_OUT }}
          >
            Questions, answered.
          </motion.h2>
          <motion.p
            className="mx-auto mb-10 mt-4 max-w-[46ch] text-on-dark/60 fluid-body-lg"
            initial={fadeUp}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1, ease: EASE_OUT }}
          >
            Still unsure? Book a call and we&apos;ll walk you through it.
          </motion.p>
          <div className="text-left">
            <FAQAccordion faqs={faqs} />
          </div>
        </div>
      </section>

      <section className="pb-[var(--fluid-section)]" aria-label="Get started">
        <div className="mx-auto max-w-[1180px] px-6 md:px-12">
          <motion.div
            className="relative flex flex-col items-center gap-7 overflow-hidden rounded-[26px] border border-hairline bg-panel-solid px-8 py-10 text-center md:flex-row md:justify-between md:gap-10 md:px-12 md:text-left"
            initial={reduce ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, ease: EASE_OUT }}
          >
            <div className="relative">
              <h2
                className="heading-pure font-heading"
                style={{
                  fontSize: "clamp(1.5rem, 1.1rem + 1.6vw, 2.25rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                  fontWeight: 480,
                }}
              >
                Ready when you are.
              </h2>
              <p className="mt-2 text-on-dark/60 fluid-body-lg">
                Start free today. Upgrade when it pays for itself, and it will.
              </p>
            </div>
            <div className="relative flex shrink-0 flex-wrap items-center justify-center gap-3">
              <CtaLink
                location="pricing-final"
                label="Start free"
                trackLead
                className="btn-gold px-7 py-3.5 text-[14px]"
              >
                Start free
                <ArrowRight size={15} strokeWidth={2} />
              </CtaLink>
              <Link href="/book" className="btn-plan px-7">
                Book a demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
